import { t } from '@avalara/bat-apps-shared/i18n';
import { AuiSupernav, SAlert, SBox, SBreadcrumbs, SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { formatPrice } from '../../../../shared/helpers';
import { isOneWebDomainRuntime } from '../../../helpers';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import analytics from '../../../lib/analytics';
import { NO_PAYMENT_COLLECTED_TOKEN } from '../../../lib/constants';
import { getAccessToken } from '../../../lib/helpers';
import { purchaseOffer, validateDiscountCode } from '../../../lib/side-effects/offers';
import { getOrderSalesTax } from '../../../lib/side-effects/order';
import { selectIsNonAdmin, selectState } from '../../../selectors';
import {
  getDiscountAmount,
  getOrderPreDiscountSubtotal,
  getOrderSubtotal,
  getOrderSummary,
  getOrderTotal,
  getProductItems,
  getSelectedPlan,
} from '../../../selectors/order';
import { setCurrentOfferPage } from '../../../store/offer';
import { receiveOrderSalesTax, selectPlan, setOrderQuantity } from '../../../store/order';
import { purchaseDeflectionWording } from '../../helpers';
import { appendQueryParamToUrl } from '../../helpers/redirect';
import PaymentMethodPanel from './payment-method-panel';
import OrderReviewSummary from './summary';
import TermsAndConditions from './terms-and-conditions';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import { Pages } from '../../../../../types/offers';

// Order Review page for products purchased through the Product Catalog / PDP flow — an alternate
// to the offer wizard's own review-order/ (which is Cybersource-driven and multi-step). Rendered
// by OfferContainer's own <Switch> at /${offerFlow}/order-review when the offer definition's
// `pages` includes Pages.OrderReview, so the usual feature-hydration/account-fetch/auth-redirect
// gating there already applies before this ever mounts. Single-plan offers auto-advance here from
// bare /${offerFlow} via OfferPlanSelect's existing plan-select-skip logic.
const OrderReview = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    account: { accountId, billingContact, isLoggedIn },
    features,
    offer,
    offer: {
      adjustableQuantity,
      maxQuantity,
      minQuantity,
      offerFlow,
      orderReviewBreadcrumbs,
      plans,
      productTierMatch: match,
      termsAndConditions,
    },
    order: { discountName, isFetchingSalesTax, quantity, salesTax },
  } = useSelector(selectState);
  const isNonAdmin = useSelector(selectIsNonAdmin);
  const productItems = useSelector(getProductItems);
  const { additionalItems = [], salesTaxAmount, showTierFinePrint } = useSelector(getOrderSummary);
  const selectedPlan = useSelector(getSelectedPlan);
  const discountAmount = useSelector(getDiscountAmount);
  const subtotal = useSelector(getOrderSubtotal);
  const total = useSelector(getOrderTotal);
  const preDiscountSubtotal = useSelector(getOrderPreDiscountSubtotal);

  const [termsChecked, setTermsChecked] = useState(false);
  const [separateAgreementChecked, setSeparateAgreementChecked] = useState(false);

  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.OrderReview));
  }, []);

  // The reverse lookup (offerFlow -> product/tier) is resolved server-side, as part of the same
  // /api/offers/:offerFlow request that already loads the rest of the offer's client details (see
  // getOfferClientDetailsWithPlans in src/server/routes/helpers/offers.ts) — if a new offer ships
  // without a matching PDP tier entry, this page still renders (and a purchase can still be placed)
  // but silently loses the tier's fine print/discount code. Surface that drift so it's discoverable
  // instead of only showing up as a visually incomplete page with no error anywhere.
  useEffect(() => {
    if (!offerFlow || match) return;

    analytics.track('Order Review: no PDP tier found for offerFlow', { offerFlow });
  }, [offerFlow, match]);

  useEffect(() => {
    if (!accountId || !isOneWebDomainRuntime() || typeof window.AvaApp?.renderAuiNav !== 'function') return;

    window.AvaApp.renderAuiNav();

    return () => {
      if (typeof window.AvaApp?.removeAuiNav === 'function') {
        window.AvaApp.removeAuiNav();
      }
    };
  }, [accountId]);

  useEffect(() => {
    if (!offerFlow || !match?.tier.discountCode) return;

    dispatch(validateDiscountCode(offerFlow, match.tier.discountCode));
  }, [offerFlow, match?.tier.discountCode]);

  // Safety net for reaching this page directly (e.g. a refresh or bookmark on
  // /${offerFlow}/order-review) rather than via bare /${offerFlow} — OfferPlanSelect's own
  // single-plan auto-advance normally selects the plan before this page ever mounts, but that
  // never runs if this URL is hit directly, leaving productItems/total empty until this fires.
  useEffect(() => {
    if (plans.length !== 1 || selectedPlan) return;

    dispatch(selectPlan({ planName: plans[0].name, planSelectComplete: true }));
  }, [plans, selectedPlan]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors order-summary/index.tsx's own effect
  useEffect(() => {
    // A fixed salesTaxAmount (see OfferOrderSummary.salesTaxAmount) means this offer already knows
    // its tax amount — skip the dynamic AvaTax lookup and set it directly so getOrderTotal and the
    // eventual purchaseOffer request (which reads state.order.salesTax) both pick it up.
    if (salesTaxAmount !== undefined) {
      dispatch(receiveOrderSalesTax(salesTaxAmount));
      return;
    }

    // state.order.salesTax is shared, offer-agnostic redux state — clear a previous offer's fixed
    // amount before this offer's own dynamic lookup resolves, so a stale value can't briefly outlive
    // the offer it belonged to (e.g. navigating from a fixed-tax offer to one without).
    dispatch(receiveOrderSalesTax(null));
    dispatch(getOrderSalesTax());
  }, [billingContact, subtotal, salesTaxAmount]);

  // Navigation on success (to /${offerFlow}/thank-you) and the error dialog on failure are both
  // already handled inside purchaseOffer's own thunk — same as BuyButton, the wizard's equivalent
  // submit control, which also fires-and-forgets this dispatch.
  const handlePlaceOrder = () => dispatch(purchaseOffer(history, NO_PAYMENT_COLLECTED_TOKEN));

  const handleBreadcrumbNav = (_event: Event, targetHref: string) => history.push(targetHref);

  // Fallback for any offer using this shared page without orderReviewBreadcrumbs configured (see
  // OfferClientDetails.orderReviewBreadcrumbs) — without it, those offers would have no in-page way
  // back to the catalog for direct visits/refreshes/bookmarks, since breadcrumbs already cover that
  // for offers that do configure them.
  const handleCancel = () => history.push(match ? `/product-catalog/${match.productKey}` : '/product-catalog');

  const hasBreadcrumbs = !!orderReviewBreadcrumbs?.length;

  const renderNavigation = () => {
    if (!accountId || !features.SHOULDSHOWPRODUCTCATALOG) return null;

    return (
      <div className='product-catalog-nav'>
        <AuiSupernav accountId={accountId.toString()} requestCurrentAccessToken={getAccessToken} />
      </div>
    );
  };

  const currency = selectedPlan?.currency;

  return (
    <>
      {renderNavigation()}
      <SContainer data-ref='buy.order-review.page'>
        {orderReviewBreadcrumbs && orderReviewBreadcrumbs.length > 0 && (
          <SBreadcrumbs
            navItems={orderReviewBreadcrumbs.map(({ labelKey, url }) => ({
              label: t(labelKey as TranslationKey),
              // Forward this page's own query params (e.g. ?shouldShowProductCatalog=true, a
              // non-prod feature-flag override) instead of baking any of them into each offer's
              // static orderReviewBreadcrumbs config — those urls are offer-agnostic paths only.
              // location.search includes its own leading '?', which appendQueryParamToUrl doesn't
              // expect (see its other callers, which always pass a bare param string).
              url: url ? appendQueryParamToUrl(url, location.search.slice(1)) : url,
              emitNavEvent: !!url,
            }))}
            navHandler={handleBreadcrumbNav}
            data-ref='buy.order-review.breadcrumb'
          />
        )}
        <h1 data-ref='buy.order-review.heading' className='pad-top-lg pad-bottom-lg'>
          {t('client.order-review.heading')}
        </h1>
        <SRow>
          <SCol span='7'>
            <SBox className='pad-all-md'>
              <OrderReviewSummary
                tier={match?.tier}
                productItems={productItems}
                additionalItems={additionalItems}
                currency={currency}
                discountAmount={discountAmount}
                discountName={discountName}
                salesTax={salesTax}
                showTierFinePrint={showTierFinePrint}
                total={total}
                adjustableQuantity={adjustableQuantity}
                quantity={quantity}
                preDiscountSubtotal={preDiscountSubtotal}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                onChangeQuantity={(newQuantity) => dispatch(setOrderQuantity({ quantity: newQuantity, offer }))}
              />
            </SBox>
          </SCol>
          <SCol span='5'>
            <SBox className='pad-all-md'>
              {isLoggedIn && isNonAdmin ? (
                <SAlert className='margin-bottom-md' status='warning' noDismiss>
                  <div data-ref='buy.order-review.purchase-deflection'>{purchaseDeflectionWording}</div>
                </SAlert>
              ) : null}
              <PaymentMethodPanel />
              <hr />
              <TermsAndConditions
                termsChecked={termsChecked}
                onTermsChange={setTermsChecked}
                separateAgreementChecked={separateAgreementChecked}
                onSeparateAgreementChange={setSeparateAgreementChecked}
                links={termsAndConditions ?? []}
              />
              <div className='flex flex-dir-col'>
                <button
                  type='button'
                  className='primary margin-bottom-md'
                  data-ref='buy.order-review.place-order-button'
                  disabled={
                    !termsChecked || !separateAgreementChecked || isFetchingSalesTax || (isLoggedIn && isNonAdmin)
                  }
                  onClick={handlePlaceOrder}
                >
                  {t('client.order-review.place-order-with-total', { total: formatPrice(total, 2, currency) })}
                </button>

                {!hasBreadcrumbs && (
                  <button type='button' className='link' data-ref='buy.order-review.cancel-link' onClick={handleCancel}>
                    {t('client.order-review.cancel-link')}
                  </button>
                )}
              </div>
            </SBox>
          </SCol>
        </SRow>
      </SContainer>
    </>
  );
};

export default OrderReview;
